import { useState } from "react";
import { Link } from "react-router-dom";
import { DateRange } from "react-day-picker";
import {
  AlertTriangle,
  ChevronRight,
  Coins,
  Crown,
  Eye,
  Gift,
  MoreVertical,
  Pencil,
  RefreshCw,
  SignalHigh,
  SignalLow,
  SignalMedium,
  Star,
  Target,
  Ticket,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DatePickerWithRange,
  perkDatePresetRange,
} from "@/components/ui/date-range-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useActiveProgramId } from "./programs/program-scope";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { initialsOf, relativeDays, titleCase } from "@/api/perkpoint/members";
import {
  InsightsApi,
  changeText,
  compact,
  insightKeys,
  monthLabel,
  type InsightDays,
} from "@/api/perkpoint/insights";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { ChartExportButton } from "./chart-controls";

type Tier = string;
type Engagement = "High" | "Medium" | "Low";
type Status = "Active" | "At risk";

const HEALTH_BAR: Record<string, string> = {
  active: "bg-green-600",
  engaged: "bg-green-500",
  at_risk: "bg-orange-400",
  inactive: "bg-slate-300",
};

function tierStyle(name: string) {
  if (name === "Bronze") {
    return {
      barClass: "bg-orange-400",
      icon: (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-orange-400 text-[10px] font-bold text-orange-600">
          B
        </span>
      ),
    };
  }
  if (name === "Silver") {
    return { barClass: "bg-slate-400", icon: <Star className="h-5 w-5 fill-slate-300 text-slate-400" /> };
  }
  if (name === "Gold") {
    return { barClass: "bg-amber-400", icon: <Crown className="h-5 w-5 text-amber-500" /> };
  }
  return { barClass: "bg-violet-400", icon: <Crown className="h-5 w-5 text-violet-500" /> };
}

const ENGAGEMENT_STYLE: Record<string, { icon: typeof Coins; iconClass: string }> = {
  earning: { icon: Coins, iconClass: "bg-green-50 text-green-700" },
  redeeming: { icon: Gift, iconClass: "bg-emerald-50 text-emerald-700" },
  campaigns: { icon: Target, iconClass: "bg-lime-50 text-lime-700" },
  gamification: { icon: Ticket, iconClass: "bg-teal-50 text-teal-700" },
  coupons: { icon: Ticket, iconClass: "bg-cyan-50 text-cyan-700" },
};

const OPPORTUNITY_STYLE: Record<string, { icon: typeof Coins; iconClass: string }> = {
  win_back: { icon: AlertTriangle, iconClass: "bg-orange-50 text-orange-600" },
  cant_lose: { icon: AlertTriangle, iconClass: "bg-red-50 text-red-600" },
  tier_push: { icon: Crown, iconClass: "bg-amber-50 text-amber-700" },
  redemption: { icon: Gift, iconClass: "bg-green-50 text-green-700" },
};

function pct(value: number | null | undefined) {
  return value == null ? "—" : `${value}%`;
}

/** The API precomputes 7, 30 and 90 days; pick the nearest to the picked range. */
function daysForRange(picked: DateRange | undefined): InsightDays {
  if (!picked?.from) return 30;
  const span = Math.round(((picked.to ?? picked.from).getTime() - picked.from.getTime()) / 864e5) + 1;
  return span <= 14 ? 7 : span <= 60 ? 30 : 90;
}

function TierCell({ tier }: { tier: Tier }) {
  if (tier === "Gold" || tier === "Platinum") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
        <Crown
          className={cn(
            "h-3.5 w-3.5",
            tier === "Platinum" ? "text-violet-500" : "text-amber-500",
          )}
        />
        {tier}
      </span>
    );
  }
  if (tier === "Silver") {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600">
        <Star className="h-3.5 w-3.5 fill-slate-300 text-slate-400" />
        Silver
      </span>
    );
  }
  if (tier !== "Bronze") {
    return <span className="text-sm font-medium text-slate-700">{tier}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-700">
      <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-orange-400 text-[8px] font-bold text-orange-600">
        B
      </span>
      Bronze
    </span>
  );
}

function EngagementCell({ engagement }: { engagement: Engagement }) {
  const Icon =
    engagement === "High"
      ? SignalHigh
      : engagement === "Medium"
        ? SignalMedium
        : SignalLow;
  const color =
    engagement === "High"
      ? "text-green-600"
      : engagement === "Medium"
        ? "text-lime-600"
        : "text-amber-500";

  return (
    <span className={cn("inline-flex items-center gap-1.5 text-sm", color)}>
      <Icon className="h-4 w-4" />
      {engagement}
    </span>
  );
}

export default function MemberInsight() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    perkDatePresetRange("last30"),
  );
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const days = daysForRange(dateRange);
  const insightsQuery = useQuery({
    queryKey: insightKeys.insights(programId ?? "", days),
    queryFn: () => InsightsApi.insights(programId!, days),
    enabled: Boolean(programId),
  });
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  const data = insightsQuery.data;
  const program = programQuery.data;
  const refreshMutation = useMutation({
    mutationFn: () => InsightsApi.refresh(programId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: insightKeys.all });
      toast.success("Insights recalculated");
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not refresh insights")),
  });

  const markers = data?.period_markers;
  const PERIOD_MARKERS = {
    pointsEarned: markers ? compact(markers.points_earned) : "—",
    pointsEarnedChange: markers ? `${changeText(markers.points_earned_change)} vs the previous period` : "—",
  };
  const activityTrendData = (data?.activity_trend ?? []).map((row) => ({ ...row, month: monthLabel(row.month) }));
  const customerMixData = (data?.customer_mix ?? []).map((row) => ({
    month: monthLabel(row.month),
    newCustomers: row.new_customers,
    repeatCustomers: row.repeat_customers,
  }));
  const memberHealth = (data?.member_health ?? []).map((row) => ({
    key: row.key,
    label: row.label,
    value: row.value,
    percent: pct(row.percent),
    barClass: HEALTH_BAR[row.key] ?? "bg-slate-300",
    width: `${row.percent}%`,
  }));
  const maxTier = Math.max(1, ...(data?.tier_distribution ?? []).map((t) => t.members));
  const tierDistribution = (data?.tier_distribution ?? []).map((row) => ({
    key: row.key,
    label: row.tier,
    members: row.members,
    percentValue: row.percent,
    percent: pct(row.percent),
    width: `${Math.round((row.members / maxTier) * 100)}%`,
    ...tierStyle(row.tier),
  }));
  const engagementBehaviour = (data?.engagement ?? []).map((row) => ({
    label: row.label,
    count: row.count == null ? "—" : row.count.toLocaleString("en-IN"),
    percent: row.percent == null ? "Not tracked" : `${row.percent}%`,
    ...(ENGAGEMENT_STYLE[row.key] ?? ENGAGEMENT_STYLE.earning),
  }));
  const topSegments = (data?.top_segments ?? []).map((row) => ({
    key: row.key,
    rank: row.rank,
    name: row.name,
    memberCount: row.members,
    members: `${row.members.toLocaleString("en-IN")} members`,
    change: row.engagement_change == null ? "No change data yet" : `${changeText(row.engagement_change)} members`,
    positive: (row.engagement_change ?? 0) >= 0,
  }));
  const attentionMembers = (data?.attention_members ?? []).map((row) => ({
    id: row.member_id,
    name: row.name || "Unnamed member",
    email: row.email || row.external_member_id,
    initials: initialsOf(row.name),
    avatarClass: "bg-emerald-100 text-emerald-800",
    tier: (row.tier ?? "—") as Tier,
    lastActivity: relativeDays(row.last_activity_at),
    points: row.points,
    engagement: titleCase(row.engagement) as Engagement,
    status: (row.status === "At risk" ? "At risk" : "Active") as Status,
  }));
  const opportunities = (data?.opportunities ?? []).map((row) => ({
    key: row.key,
    title: row.title,
    description: row.description,
    ...(OPPORTUNITY_STYLE[row.key] ?? OPPORTUNITY_STYLE.redemption),
  }));
  // Prefer API key "redemption" (e.g. “Not keeping up…”) when listing opportunities
  const orderedOpportunities = [...opportunities].sort((a, b) =>
    a.key === "redemption" ? -1 : b.key === "redemption" ? 1 : 0,
  );

  const startingFromProgram =
    program?.tiers?.find((tier) => tier.is_starting) ?? null;
  const baseTierName =
    startingFromProgram?.name?.trim() ||
    (program?.startingTier?.trim() ? program.startingTier.trim() : null);
  const baseTierKey = startingFromProgram?.key?.trim() || null;

  const baseTierRow =
    tierDistribution.find(
      (tier) =>
        (baseTierKey && tier.key === baseTierKey) ||
        (baseTierName &&
          tier.label.toLowerCase() === baseTierName.toLowerCase()),
    ) ?? null;
  const otherTier = tierDistribution
    .filter((tier) => tier !== baseTierRow)
    .slice(0, 2);

  const formatTierMix = (
    tier: (typeof tierDistribution)[number] | null | undefined,
  ) => {
    if (!tier) return null;
    return `${tier.label} holds ${tier.members.toLocaleString("en-IN")} members (${tier.percent})`;
  };

  const tierMixSentenceParts = [
    formatTierMix(baseTierRow) ??
      (baseTierName
        ? `${baseTierName} has no members in this mix yet`
        : tierDistribution.length === 0
          ? "No members have a tier yet"
          : null),
    ...otherTier.map((tier) => formatTierMix(tier)),
  ].filter(Boolean) as string[];

  const tierMixSentence =
    tierMixSentenceParts.length > 0
      ? `${tierMixSentenceParts.join(". ")}.`
      : "Tier mix will show here once members are enrolled.";

  const activeHealth = memberHealth.find(
    (item) => item.key === "active" || item.label === "Active",
  );
  const atRiskHealth = memberHealth.find(
    (item) => item.key === "at_risk" || /at\s*risk/i.test(item.label),
  );
  const inactiveHealth = memberHealth.find(
    (item) => item.key === "inactive" || item.label === "Inactive",
  );
  const atRiskSegment = topSegments.find(
    (segment) =>
      segment.key === "at_risk" || segment.name.toLowerCase() === "at risk",
  );
  const earners = engagementBehaviour.find((item) =>
    item.label.startsWith("Members earning"),
  );
  const redeemers = engagementBehaviour.find((item) =>
    item.label.startsWith("Members redeeming"),
  );
  const latestTrend = activityTrendData[activityTrendData.length - 1] ?? { month: "—", active: 0, earned: 0, redeemed: 0 };
  const latestMix = customerMixData[customerMixData.length - 1] ?? { month: "—", newCustomers: 0, repeatCustomers: 0 };
  const atRiskOpportunity = opportunities.find(
    (item) =>
      item.key === "win_back" ||
      item.key === "cant_lose" ||
      item.key === "at_risk" ||
      /quiet|risk|win.?back|stale/i.test(item.title),
  );
  const redemptionOpp = opportunities.find((item) => item.key === "redemption");

  const atRiskCount = atRiskHealth?.value ?? 0;
  const whoToSaveSentence =
    atRiskCount <= 0
      ? "No members are marked at risk in this period. As activity builds, this card will show who needs a win-back."
      : [
          atRiskOpportunity?.description ??
            "These members need a win-back before they drop off.",
          `They are ${atRiskHealth?.percent ?? "—"} of members.`,
          atRiskSegment
            ? `The ${atRiskSegment.name} segment has ${atRiskSegment.members}.`
            : "",
        ]
          .filter(Boolean)
          .join(" ");

  const decisions: {
    id: string;
    eyebrow: string;
    value: string;
    valueHint?: string;
    sentence: string;
    action: string;
    href: string;
  }[] = [
    {
      id: "save",
      eyebrow: "Who to save",
      value: atRiskCount.toLocaleString("en-IN"),
      valueHint: atRiskCount > 0 ? "at-risk members" : "at-risk members right now",
      sentence: whoToSaveSentence,
      action: "Open the member list",
      href: "/perkpoint/member/all",
    },
    {
      id: "liability",
      eyebrow: "Where liability sits",
      value: PERIOD_MARKERS.pointsEarned,
      sentence: `Points earned in the last ${days} days, ${PERIOD_MARKERS.pointsEarnedChange}. ${latestTrend.month} earned ${latestTrend.earned.toLocaleString("en-IN")} and redeemed ${latestTrend.redeemed.toLocaleString("en-IN")}, so unredeemed points are still growing.`,
      action: "Review earn and redeem",
      href: "/perkpoint/member/history",
    },
    {
      id: "tier",
      eyebrow: "Which tier is the base",
      value: baseTierRow?.percent ?? "—",
      valueHint: baseTierName
        ? `of members on ${baseTierName}`
        : "of members on the base tier",
      sentence: `Base tier: ${baseTierName ?? "—"}. ${tierMixSentence} This page has the mix, not a tier trend, so the next step is the tier screen.`,
      action: "View tiers",
      href: "/perkpoint/rewards/tiers",
    },
    {
      id: "redeem",
      eyebrow: "Whether redemption is healthy",
      value: redeemers?.percent ?? "—",
      sentence:
        redemptionOpp?.description ??
        `${redeemers?.count ?? "—"} members redeemed a reward, against ${earners?.count ?? "—"} still earning (${earners?.percent ?? "—"}). Redemption is real, and it is not keeping up with earn.`,
      action: "See redeem history",
      href: "/perkpoint/member/history",
    },
  ];

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-8 pb-4">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Member Insights
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500 sm:text-base">
            Who is earning, who is redeeming, and who needs attention in the
            selected dates.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end">
          <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
            {data ? (
              <span className={cn(data.stale && "text-amber-700")}>
                {data.stale ? "Out of date · " : ""}Last {data.period.days} days · updated{" "}
                {new Date(data.computed_at).toLocaleString("en-GB", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={!programId || refreshMutation.isPending}
              onClick={() => refreshMutation.mutate()}
            >
              <RefreshCw className={cn("mr-1 h-3.5 w-3.5", refreshMutation.isPending && "animate-spin")} />
              Refresh
            </Button>
          </div>
        <DatePickerWithRange
          presets
          date={dateRange}
          setDate={setDateRange}
          className="w-full sm:w-auto [&_button]:h-11 [&_button]:rounded-full [&_button]:bg-white [&_button]:text-slate-700"
        />
        </div>
      </header>

      {!data ? (
        <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-muted-foreground">
          {insightsQuery.isError
            ? apiErrorMessage(insightsQuery.error, "Could not load insights")
            : "Loading insights…"}
        </p>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-2">
        {decisions.map((decision) => (
          <article
            key={decision.id}
            className="flex flex-col rounded-[24px] bg-white p-5 ring-1 ring-black/[0.06]"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {decision.eyebrow}
            </p>
            <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
              {decision.value}
            </p>
            {decision.valueHint ? (
              <p className="mt-1 text-xs font-medium text-slate-500">
                {decision.valueHint}
              </p>
            ) : null}
            <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
              {decision.sentence}
            </p>
            <Link
              to={decision.href}
              className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-800 hover:text-emerald-950"
            >
              {decision.action}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </article>
        ))}
      </section>

      <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
        <CardContent className="p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-950">
              New vs repeat customers
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
              Repeat customers are the base. {latestMix.month} brought in{" "}
              {latestMix.newCustomers.toLocaleString("en-IN")} new members
              against {latestMix.repeatCustomers.toLocaleString("en-IN")} who
              came back.
            </p>
            </div>
            <ChartExportButton
              filename={`customer-mix-${days}d.csv`}
              rows={customerMixData.map((row) => ({
                Month: row.month,
                "New customers": row.newCustomers,
                "Repeat customers": row.repeatCustomers,
              }))}
            />
          </div>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={customerMixData} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="newCustomers"
                  name="New customers"
                  fill="#86efac"
                  radius={[6, 6, 0, 0]}
                />
                <Bar
                  dataKey="repeatCustomers"
                  name="Repeat customers"
                  fill="#1B7A45"
                  radius={[6, 6, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Charts row */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                  Member activity
                </h2>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
                  Active members, earn, and redeem all rise from January to{" "}
                  {latestTrend.month}. Redeem still trails earn —{" "}
                  {latestTrend.month} redeemed{" "}
                  {latestTrend.redeemed.toLocaleString("en-IN")} against{" "}
                  {latestTrend.earned.toLocaleString("en-IN")} earned — so the
                  liability decision above is the gap on this chart.
                </p>
              </div>
              <ChartExportButton
                filename={`member-activity-${days}d.csv`}
                rows={activityTrendData.map((row) => ({
                  Month: row.month,
                  "Active members": row.active,
                  "Points earned": row.earned,
                  "Points redeemed": row.redeemed,
                }))}
              />
            </div>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activityTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="active"
                    name="Active members"
                    stroke="#1B7A45"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="earned"
                    name="Points earned"
                    stroke="#22c55e"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="redeemed"
                    name="Points redeemed"
                    stroke="#86efac"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-2">
          <CardContent className="flex h-full flex-col p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                Member health
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">
                {activeHealth?.percent} are active. The{" "}
                {(atRiskHealth?.value ?? 0).toLocaleString("en-IN")} at-risk
                members are the save list. Inactive (
                {(inactiveHealth?.value ?? 0).toLocaleString("en-IN")}) is a
                similar
                size and is not in that queue yet.
              </p>
              </div>
              <ChartExportButton
                filename={`member-health-${days}d.csv`}
                rows={memberHealth.map((item) => ({
                  Status: item.label,
                  Members: item.value,
                  Share: item.percent,
                }))}
              />
            </div>
            <div className="space-y-4">
              {memberHealth.map((item) => (
                <div key={item.label}>
                  <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-700">
                      {item.label}
                    </span>
                    <span className="text-slate-600">
                      {item.value.toLocaleString("en-IN")}{" "}
                      <span className="text-muted-foreground">
                        ({item.percent})
                      </span>
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={cn("h-full rounded-full", item.barClass)}
                      style={{ width: item.width }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/perkpoint/member/all"
              className="mt-auto pt-6 text-left text-sm font-semibold text-emerald-800"
            >
              View member list →
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Distribution / behaviour / segments */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="flex h-full flex-col p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                Member distribution by tier
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">
                Base tier: {baseTierName ?? "—"}. {tierMixSentence} There is
                no month-by-month tier series here, so this is the mix, not a
                movement.
              </p>
              </div>
              <ChartExportButton
                filename={`tier-distribution-${days}d.csv`}
                rows={tierDistribution.map((tier) => ({
                  Tier: tier.label,
                  Members: tier.members,
                  Share: tier.percent,
                }))}
              />
            </div>
            <div className="space-y-4">
              {tierDistribution.map((tier) => (
                <div key={tier.label}>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                      {tier.icon}
                      {tier.label}
                    </div>
                    <div className="text-right text-sm text-slate-600">
                      {tier.members.toLocaleString("en-IN")} members
                      <span className="ml-1 text-muted-foreground">
                        ({tier.percent})
                      </span>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={cn("h-full rounded-full", tier.barClass)}
                      style={{ width: tier.width }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/perkpoint/rewards/tiers"
              className="mt-auto pt-6 text-left text-sm font-semibold text-emerald-800"
            >
              View tiers →
            </Link>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Engagement behaviour
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                How members interact with the program
              </p>
              </div>
              <ChartExportButton
                filename={`engagement-${days}d.csv`}
                rows={(data?.engagement ?? []).map((row) => ({
                  Behaviour: row.label,
                  Members: row.count,
                  Share: row.percent,
                }))}
              />
            </div>
            <div className="space-y-3">
              {engagementBehaviour.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className={cn(
                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                        item.iconClass,
                      )}
                    >
                      <item.icon className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-medium text-slate-800">
                      {item.label}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-slate-900">
                      {item.count}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.percent}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06]">
          <CardContent className="flex h-full flex-col p-5">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Top member segments
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Highest engagement opportunities
              </p>
              </div>
              <ChartExportButton
                filename={`top-segments-${days}d.csv`}
                rows={(data?.top_segments ?? []).map((segment) => ({
                  Rank: segment.rank,
                  Segment: segment.name,
                  Members: segment.members,
                  "Engagement change": segment.engagement_change,
                }))}
              />
            </div>
            <div className="space-y-3">
              {topSegments.map((segment) => (
                <div
                  key={segment.rank}
                  className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-3"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-sm font-semibold text-green-700">
                    {segment.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{segment.name}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {segment.members}
                    </p>
                    <p
                      className={cn(
                        "mt-1 text-sm font-medium",
                        segment.positive ? "text-green-700" : "text-red-600",
                      )}
                    >
                      {segment.change}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/perkpoint/member/segments"
              className="mt-auto pt-6 text-left text-sm font-semibold text-emerald-800"
            >
              View segments →
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Attention + opportunities */}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-3">
          <CardContent className="p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Members needing attention
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">
                  These rows are the save list from the decision above. The
                  line under each name is the visit gap and the points still
                  on the balance.
                </p>
              </div>
              <Link
                to="/perkpoint/member/all"
                className="shrink-0 text-sm font-semibold text-emerald-800"
              >
                View all →
              </Link>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                    <TableHead>Member</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead>Points balance</TableHead>
                    <TableHead>Engagement</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attentionMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback
                              className={cn(
                                "text-xs font-semibold",
                                member.avatarClass,
                              )}
                            >
                              {member.initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-950">
                              {member.name}
                            </p>
                            <p className="truncate text-sm text-slate-500">
                              {member.email}
                            </p>
                            <p className="mt-1 text-xs font-medium text-amber-800">
                              No visit in{" "}
                              {member.lastActivity.replace(/ ago$/i, "")} ·{" "}
                              {member.points.toLocaleString("en-IN")} pts still
                              on the balance
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TierCell tier={member.tier} />
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {member.lastActivity}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-sm font-medium text-green-800">
                          <Coins className="h-3.5 w-3.5" />
                          {member.points.toLocaleString("en-IN")} pts
                        </span>
                      </TableCell>
                      <TableCell>
                        <EngagementCell engagement={member.engagement} />
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <span
                            className={cn(
                              "h-2 w-2 rounded-full",
                              member.status === "Active"
                                ? "bg-green-500"
                                : "bg-orange-400",
                            )}
                          />
                          {member.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="mr-2 h-4 w-4" />
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-0 bg-white shadow-none ring-1 ring-black/[0.06] xl:col-span-2">
          <CardContent className="p-5">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">
                Member opportunities
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Suggested actions to improve retention
              </p>
            </div>
            <div className="space-y-3">
              {orderedOpportunities.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  className="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition-colors hover:bg-slate-50"
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      item.iconClass,
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
